import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { getUserFullName } from 'utils';
import usePastTime from 'hooks/usePastTime';
import useDataRoomDocument from 'hooks/useDataRoomDocument';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import TruckIcon from 'icons/Truck.icon';
import EditIcon from 'icons/Pencil.icon';
import ProductsIcon from 'icons/Products.icon';
import DownloadIcon from 'icons/Download.icon';
import TrashIcon from 'icons/Trash.icon';
import MessageIcon from 'icons/Message.icon';
import LinkIcon from 'icons/Link.icon';

import './DataRoomDocumentMobileModal.styles.scss';

type DataRoomDocumentMobileModalProps = {
  className?: string;
  node: DataRoomNode;
  canTagFiles: boolean;
  canBuyerAskQuestion: boolean;
  canUserCreateShareableLinks: boolean;
  onMove: () => void;
  onManage: () => void;
  onRename: () => void;
  onTag: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onAskQuestion: () => void;
  onShareFiles: () => void;
};

const DataRoomDocumentMobileModal: React.ForwardRefRenderFunction<
  ModalRef,
  DataRoomDocumentMobileModalProps
> = (props, ref) => {
  const {
    className,
    node,
    canTagFiles,
    canBuyerAskQuestion,
    canUserCreateShareableLinks,
    onMove,
    onManage,
    onRename,
    onTag,
    onDelete,
    onDownload,
    onAskQuestion,
    onShareFiles,
  } = props;

  const { name, dataRoomOwner, createdAt, hasAuthorizedActions } = node || {};

  const { isFolder, canDownload, canShareNode } = useDataRoomDocument(node);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('data-room-document-mobile-modal', className);

  const { t } = useTranslation();

  const pastTime = usePastTime();

  const onAction = useCallback(
    (e: React.MouseEvent, action: () => void) => {
      e.stopPropagation();
      e.preventDefault();

      action();

      modal?.close();
    },
    [modal],
  );

  return (
    <Modal className={classes} title={name} lockScroll={false} ref={modalRef}>
      <p className="document-info">
        <span>
          {t('addedBy', {
            fullName: getUserFullName(
              dataRoomOwner?.firstName,
              dataRoomOwner?.lastName,
            ),
          })}
        </span>

        <span>{pastTime(createdAt)}</span>
      </p>

      <div className="modal-actions-row">
        {canShareNode && hasAuthorizedActions ? (
          <>
            <Button
              variant="outline"
              icon={GroupUsersIcon}
              onClick={(e) => onAction(e, onManage)}
            >
              {t('shareFile')}
            </Button>
            {canUserCreateShareableLinks && (
              <Button
                variant="outline"
                icon={LinkIcon}
                onClick={(e) => onAction(e, onShareFiles)}
              >
                {t('createSecureLink')}
              </Button>
            )}
          </>
        ) : null}

        {hasAuthorizedActions && (
          <Button
            variant="outline"
            icon={TruckIcon}
            onClick={(e) => onAction(e, onMove)}
          >
            {t('move')}
          </Button>
        )}

        {hasAuthorizedActions && (
          <Button
            variant="outline"
            icon={EditIcon}
            onClick={(e) => onAction(e, onRename)}
          >
            {t('rename')}
          </Button>
        )}

        {hasAuthorizedActions && canTagFiles && !isFolder && (
          <Button
            variant="outline"
            icon={ProductsIcon}
            onClick={(e) => onAction(e, onTag)}
          >
            {t('tagThisFile')}
          </Button>
        )}

        {canDownload && (
          <Button
            variant="outline"
            icon={DownloadIcon}
            onClick={(e) => onAction(e, onDownload)}
          >
            {t('download')}
          </Button>
        )}

        {canBuyerAskQuestion && !isFolder && (
          <Button
            variant="outline"
            icon={MessageIcon}
            onClick={(e) => onAction(e, onAskQuestion)}
          >
            {t('askAQuestion')}
          </Button>
        )}

        {hasAuthorizedActions && (
          <Button
            className="alert"
            variant="outline"
            icon={TrashIcon}
            onClick={(e) => onAction(e, onDelete)}
          >
            {t('delete')}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default forwardRef(DataRoomDocumentMobileModal);
